import { v } from 'h3-valibot';
import type {
  CartFragment,
  MoneyFragment,
  ShippingCartAddressFragment
} from '~/lib/Shop/generated/schema';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { type ActionModel } from '~/stores/models';

export enum CartTypes {
  CART = 'cart',
  CUSTOMER_CART = 'customerCart',
  REQUEST_CART = 'requestCart'
}

export type CartType =
  | CartTypes.CART
  | CartTypes.REQUEST_CART
  | CartTypes.CUSTOMER_CART;

const modifyCartItemBySku = v.object({
  quantity: v.number(),
  sku: v.string(),
  config_id: v.optional(v.string())
});
const modifyCartItemByDecoratedProduct = v.object({
  quantity: v.number(),
  config_id: v.optional(v.string()),
  decoratedProduct: v.instance(DecoratedProduct)
});

export type ModifyCartItemByDecoratedProduct = v.InferOutput<
  typeof modifyCartItemByDecoratedProduct
>;

export const modifyCartItemSchema = v.union([
  modifyCartItemBySku,
  modifyCartItemByDecoratedProduct
]);

export type EComDataType = {
  currency?: string;
  value: number;
  items: ReadonlyArray<any>;
  ecom_source: string;
  tax?: MoneyFragment;
  transaction_id?: string;
  coupon?: string;
  shipping?: ShippingCartAddressFragment;
  payment_type?: string;
  checkout_step?: string;
  cart_id?: string;
};

export type ModifyCartItem = v.InferOutput<typeof modifyCartItemSchema>;

export type UpdateCartItem = {
  uid: string;
  quantity: number;
  config_id: string;
};

export type CartActionType =
  | 'mergeCarts'
  | 'addItems'
  | 'removeItem'
  | 'updateItems'
  | 'addShippingAddress'
  | 'addBillingAddress'
  | 'addDeliveryMethod'
  | 'setPaymentMethod'
  | 'setAdyenStateData'
  | 'addAdyenPaymentDetails'
  | 'loadAgreements'
  | 'addCoupon'
  | 'removeCoupon'
  | 'checkTaxExemption'
  | 'uploadTaxExemption'
  | 'removeTaxExemption'
  | 'loadCart'
  | 'placeQuote'
  | 'placeOrder';

export type CartActionModel = ActionModel<CartActionType>;

export type AddProductsOptions = {
  /**
   * The source the product was added from.
   * Used in analytic event. If not present, a default source
   * will be used.
   *
   * @see https://gcp.baslerweb.com/jira/browse/WEB2-2439
   */
  source?: string;
};

export type CartStoreState = {
  /**
   * @deprecated use `$shopStore` from `useNuxtApp` instead.
   */
  store: string;
  /**
   * @deprecated use `$locale` from `useNuxtApp` instead.
   */
  locale: string;
  lastAction: CartActionModel | null;
  /**
   * Loading indicator for __every__ async api action performed in this.
   * If a more detailed loading state is necessary, this could be moved to
   * a getter.
   */
  loading: boolean;
  /**
   * All available carts and **loaded** carts.
   * A cart might exist (cookie) but is not loaded. In that, the
   * value will be `null`!
   */
  carts: Record<CartType, CartFragment | null>;
  relevantCarts: Record<CartType, CartFragment | null>;
  activeCartType: CartType;
};
